import { useEffect, useRef, useState } from 'react';

import { useAtom, useAtomValue } from 'jotai';

import { filtersListAtom } from '@core/Atoms/Plp/FiltersListAtom';
import { postcodeAtom } from '@core/Atoms/Postcode/Postcode.atom';
import { locationAtom } from '@core/Atoms/Search/LocationAtom';
import { FILTERS_PROPS, POSTCODE_KEY } from '@core/Constants/Filters/LocationFilter.constants';
import type { LocationState } from '@core/Entities/Search/LocationState.entity';
import type { Filter } from '@core/Entities/Search/SearchFilters/Filter.entity';
import { debounce } from '@core/Utils/Debounce';
import { getFilterFromArray } from '@core/Utils/SearchFilters/GetFilterFromArray';
import validatePostcode from '@core/Utils/Validation/ValidatePostcode';
import { getClosestIndexInRange } from '@plp/Utils/Filters/GetClosestIndexInRange';
import { useUpdateFilters } from '@search-filters/Hooks/UseUpdateFilters';

import { createArrayRange } from '../../../../Utils/CreateArrayRange';
import { trackFilterEvent } from '../../Filters.tracking';
import { DISTANCE_STEP, MAX_DISTANCE, MAX_DISTANCE_KEY } from './LocationFilter.constants';

const useLocationFilterUk = (errorMessage: string, applyButton?: boolean) => {
  const filtersList = useAtomValue(filtersListAtom);
  const [savedPC, setPC] = useAtom(postcodeAtom);
  const [locationState, setLocationState] = useAtom(locationAtom);
  const hasUserTouchedType = useRef(false);

  const isLocPlp = !!getFilterFromArray('area', filtersList)?.value;
  const DISTANCE_RANGE = createArrayRange(5, MAX_DISTANCE, DISTANCE_STEP);
  const postcode = getFilterFromArray(POSTCODE_KEY, filtersList)?.value as string | undefined;
  const distance =
    getFilterFromArray(MAX_DISTANCE_KEY, filtersList)?.value ||
    DISTANCE_RANGE[DISTANCE_RANGE.length - 1];

  const [inputValue, setInputValue] = useState(postcode ? postcode : savedPC);
  const [isValid, setIsValid] = useState(!!inputValue);
  const [error, setError] = useState(inputValue ? '' : errorMessage);

  const { updateFilters } = useUpdateFilters();

  const [pendingFilters, setPendingFilters] = useState<Filter[]>([]);
  const [localSliderValue, setLocalSliderValue] = useState<any>(distance);
  const [localSliderDisplayValue, setLocalSliderDisplayValue] = useState<any>(distance);
  const [pendingLocationState, setPendingLocationState] = useState<LocationState>({
    isValid: false,
    location: {
      lat: 0,
      lon: 0,
    },
    error: false,
  });
  const [pendingPC, setPendingPC] = useState('');

  const validateAndUpdate = async () => {
    const response = await validatePostcode(inputValue);
    if (response) {
      const { isValid, location } = response;
      setIsValid(isValid);

      if (isValid || inputValue === '') {
        const locStore = applyButton ? setPendingLocationState : setLocationState;
        const pcStore = applyButton ? setPendingPC : setPC;
        locStore({
          ...locationState,
          isValid,
          ...(location && { location: { lat: location.lat, lon: location.lon } }),
        });
        pcStore(inputValue);
      }
      if (isValid) {
        setError('');
        if (applyButton) {
          setPendingFilters([
            { id: POSTCODE_KEY, value: inputValue },
            { id: 'lat', value: location.lat.toString() },
            { id: 'lon', value: location.lon.toString() },
            { id: 'region', value: undefined },
            { id: 'area', value: undefined },
            { id: 'dealer-id', value: undefined },
            { id: 'dealership-id', value: undefined },
          ]);
        } else {
          updateFilters([
            { id: POSTCODE_KEY, value: inputValue },
            { id: 'lat', value: location.lat.toString() },
            { id: 'lon', value: location.lon.toString() },
            { id: 'region', value: undefined },
            { id: 'area', value: undefined },
            { id: 'dealer-id', value: undefined },
            { id: 'dealership-id', value: undefined },
          ]);
        }
      } else {
        setError(errorMessage);
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    hasUserTouchedType.current = true;
    const newPostcodeValue = e.target.value;
    trackFilterEvent(POSTCODE_KEY, newPostcodeValue, filtersList);
    setInputValue(newPostcodeValue.toUpperCase());
  };

  const handleInputBlur = async () => {
    await validateAndUpdate();
  };

  const resetAllFilter = () => {
    const filtersToReset = FILTERS_PROPS.filter(
      filter =>
        (!filter.restrictLocales || filter.restrictLocales?.includes('uk')) &&
        filter.filterType === POSTCODE_KEY,
    ).map(filter => {
      return { id: filter.key, value: undefined };
    });
    updateFilters(filtersToReset);
    setInputValue('');
    setIsValid(false);
    setError(errorMessage);
    setPC('');
    setLocationState({
      isValid: false,
      location: {
        lat: 0,
        lon: 0,
      },
      error: false,
    });
  };

  const canReset = () => {
    return !!inputValue || selectedRangeIndexes[0] !== DISTANCE_RANGE.length - 1;
  };

  const onSliderAfterChange = (values: [number, number]) => {
    const filterValue =
      values[0] === DISTANCE_RANGE.length - 1 ? undefined : DISTANCE_RANGE[values[0]].toString();
    setLocalSliderValue(values[0]);
    setLocalSliderDisplayValue(DISTANCE_RANGE[values[0]]);
    if (applyButton) {
      setPendingFilters([{ id: MAX_DISTANCE_KEY, value: filterValue }]);
    } else {
      trackFilterEvent(MAX_DISTANCE_KEY, 'All', filtersList);
      updateFilters([{ id: MAX_DISTANCE_KEY, value: filterValue }]);
    }
  };

  const selectedRangeIndexes: [number] = [
    getClosestIndexInRange(Number(distance || 1), DISTANCE_RANGE),
  ];
  const stepCount = DISTANCE_RANGE.length;

  // This handles the situation where the page is loaded with a postcode in the searchParams
  // But that isnt be available in the filtersAtom at the time this hook is first run
  useEffect(() => {
    if ((!!postcode && postcode !== inputValue) || (postcode === '' && postcode !== inputValue)) {
      setInputValue(postcode);
      setError('');
      setIsValid(true);
    }
  }, [postcode]);

  useEffect(() => {
    if (hasUserTouchedType.current) {
      debounce(() => validateAndUpdate(), 250)();
    }
  }, [inputValue, hasUserTouchedType]);

  useEffect(() => {
    const pcInFilters = getFilterFromArray(POSTCODE_KEY, filtersList)?.value;
    if (pcInFilters) {
      setInputValue(pcInFilters as string);
    }
  }, [filtersList]);

  const handleApply = async () => {
    setPC(pendingPC);
    setLocationState(pendingLocationState as LocationState);
    trackFilterEvent(POSTCODE_KEY, inputValue, filtersList);
    updateFilters([...pendingFilters]);
  };

  return {
    postcode: inputValue,
    resetAllFilter,
    canReset,
    handleInputBlur,
    handleInputChange,
    error,
    validateAndUpdate,
    sliderProps: {
      onAfterChange: onSliderAfterChange,
      stepCount,
      isSingleKnob: true,
      disabled: !isValid,
      selectedRangeIndexes: applyButton ? [localSliderValue] : selectedRangeIndexes,
    },
    isValid,
    handleApply,
    localSliderDisplayValue,
    shouldHideRangeFilters: isLocPlp,
  };
};

export default useLocationFilterUk;
